import { roundToStep } from '@/domains/pages/shared/chart-axis';

/** Dot diameter range on a segment scale, in px. Area scales with the segment weight. */
const DOT_MIN_PX = 12;
const DOT_MAX_PX = 26;
export const MAX_DOT_RADIUS_PX = DOT_MAX_PX / 2;

/** Approximate px per character at text-xs, used to reserve horizontal room for a label. */
const LABEL_CHAR_PX = 6.2;
/** Minimum clear space between two labels sharing a lane. */
export const LABEL_GAP_PX = 12;

export const LABEL_EDGE_LEFT_PERCENT = 8;
export const LABEL_EDGE_RIGHT_PERCENT = 92;

export type SegmentScaleSegment = {
  name: string;
  /** Humanized label when legacy tag mapping is active. */
  displayName?: string;
  /** Position along the axis. Segments without a value are dropped. */
  value: number | null;
  /** Relative dot area: headcount on the proficiency scale, spend on the AI spend scale. */
  weight: number;
};

export type SegmentScalePoint = {
  name: string;
  displayName: string;
  value: number;
  weight: number;
  /** 0-100 position along the rendered axis. */
  percent: number;
  diameterPx: number;
};

export type LabelCandidate = {
  name: string;
  widthPx: number;
  percent: number;
  /** Higher wins a collision. The caller gives the selected dot the top priority. */
  priority: number;
};

/** Position of a value on an axis clamped to [min, max], as a 0-100 percentage. */
export function scaleToPercent(value: number, min: number, max: number): number {
  if (!Number.isFinite(value) || max <= min) return 0;
  const ratio = (value - min) / (max - min);
  return Math.min(100, Math.max(0, ratio * 100));
}

/** Dot area is proportional to the weight, so diameter follows the square root. */
export function dotDiameterPx(weight: number, maxWeight: number): number {
  if (maxWeight <= 0 || weight <= 0) return DOT_MIN_PX;
  const ratio = Math.sqrt(Math.min(weight, maxWeight) / maxWeight);
  return DOT_MIN_PX + ratio * (DOT_MAX_PX - DOT_MIN_PX);
}

export function estimateLabelWidthPx(name: string, valueLabel: string): number {
  return `${name} ${valueLabel}`.length * LABEL_CHAR_PX;
}

export function buildAxisTicks(min: number, max: number, stepSize: number): number[] {
  if (!Number.isFinite(stepSize) || stepSize <= 0 || max <= min) return [min, max];

  const ticks: number[] = [];
  const lastIndex = Math.floor((max - min) / stepSize + 1e-9);
  for (let index = 0; index <= lastIndex; index += 1) {
    ticks.push(roundToStep(min + index * stepSize, stepSize));
  }
  return ticks;
}

/** Keeps a label inside the plot when its dot sits near either end. */
export function labelTransform(percent: number): string {
  if (percent <= LABEL_EDGE_LEFT_PERCENT) return 'translateX(0)';
  if (percent >= LABEL_EDGE_RIGHT_PERCENT) return 'translateX(-100%)';
  return 'translateX(-50%)';
}

/** Rendered px bounds of a label, mirroring the edge clamping in labelTransform. */
export function labelBoundsPx(
  percent: number,
  widthPx: number,
  containerWidthPx: number,
): { left: number; right: number } {
  const center = (percent / 100) * containerWidthPx;

  if (percent <= LABEL_EDGE_LEFT_PERCENT) {
    return { left: center, right: center + widthPx };
  }
  if (percent >= LABEL_EDGE_RIGHT_PERCENT) {
    return { left: center - widthPx, right: center };
  }
  return { left: center - widthPx / 2, right: center + widthPx / 2 };
}

/**
 * Picks which labels fit in a single lane. Candidates are placed highest priority first and a
 * label is dropped when it would overlap one already placed, so the dropped dot stays reachable
 * through its hover tooltip.
 */
export function selectVisibleLabels(
  candidates: LabelCandidate[],
  containerWidthPx: number,
): Set<string> {
  const sorted = [...candidates].sort((a, b) => b.priority - a.priority);
  const placed: Array<{ left: number; right: number }> = [];
  const visible = new Set<string>();

  for (const candidate of sorted) {
    const bounds = labelBoundsPx(candidate.percent, candidate.widthPx, containerWidthPx);
    const overlaps = placed.some(
      (interval) => bounds.left < interval.right + LABEL_GAP_PX && bounds.right > interval.left - LABEL_GAP_PX,
    );
    if (!overlaps) {
      placed.push(bounds);
      visible.add(candidate.name);
    }
  }

  return visible;
}

/** Builds the rendered points for the scale chart, ordered by value. */
export function buildSegmentScalePoints(
  segments: SegmentScaleSegment[],
  min: number,
  max: number,
): SegmentScalePoint[] {
  const valued = segments
    .filter((segment): segment is SegmentScaleSegment & { value: number } => segment.value != null)
    .sort((a, b) => a.value - b.value);

  const maxWeight = Math.max(0, ...valued.map((segment) => segment.weight));

  return valued.map((segment) => ({
    name: segment.name,
    displayName: segment.displayName ?? segment.name,
    value: segment.value,
    weight: segment.weight,
    percent: scaleToPercent(segment.value, min, max),
    diameterPx: dotDiameterPx(segment.weight, maxWeight),
  }));
}
